'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address, Fetchable } from '@morpho-org/blue-sdk';
import { useReadOnlyVault } from '@morpho-org/dapp-core';
import { type CSSProperties, useMemo } from 'react';
import type { VaultV2Gates } from '@/helpers/vault';
import type { DepositDisabledReason } from '@/helpers/warnings';
import { isVaultV2ExitGated, isVaultV2Gated } from '@/helpers/vault';
import { DEPOSIT_DISABLED_MESSAGE } from '@/helpers/warnings';
export interface UseVaultV2TooltipMessageProps {
  address: Address;
  error: Fetchable<string>;
  chainId?: ChainId;
  gates?: VaultV2Gates;
  /** Whether the tooltip belongs to the deposit flow. Entry-only gates restrict deposits only. */
  isDeposit?: boolean;
  isDepositDisabled?: boolean;
  /**
   * Why deposits are blocked. Carries the explanation on surfaces that show no vault warning
   * card (the position-manager dialogs), where the tooltip is the only place to give it.
   */
  depositDisabledReason?: DepositDisabledReason;
}

/**
 * Hook that returns the tooltip message and props for vault v2 operations.
 *
 * @param props - The props for the tooltip message.
 * @param props.address - The address of the vault.
 * @param props.error - The error message.
 * @param props.chainId - The chain ID of the vault.
 * @param props.gates - The vault's gates. Any gate restricts deposits; only an exit gate restricts withdrawals.
 * @param props.isDeposit - Whether the tooltip belongs to the deposit flow.
 * @returns The tooltip message and text style.
 * @internal
 */
export const useVaultV2TooltipMessage = t0 => {
  const $ = _c(15);
  const {
    address,
    error,
    chainId,
    gates,
    isDeposit,
    isDepositDisabled,
    depositDisabledReason
  } = t0;
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = {
      chainId,
      address
    };
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    isReadOnly,
    message
  } = useReadOnlyVault(t1);
  const isExitGated = isVaultV2ExitGated(gates);
  const isGateBlocking = isDeposit ? isVaultV2Gated(gates) : isExitGated;
  let t2;
  bb0: {
    if (isReadOnly) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          whiteSpace: "break-spaces"
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] !== message) {
        t4 = {
          message,
          textStyle: t3
        };
        $[4] = message;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      t2 = t4;
      break bb0;
    }
    if (isDepositDisabled) {
      const t3 = DEPOSIT_DISABLED_MESSAGE[depositDisabledReason ?? "deposit_disabled"];
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          whiteSpace: "break-spaces"
        };
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      let t5;
      if ($[7] !== t3) {
        t5 = {
          message: t3,
          textStyle: t4
        };
        $[7] = t3;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t2 = t5;
      break bb0;
    }
    if (isGateBlocking) {
      const t3 = isExitGated ? "Access to this vault is restricted by an active gate. Deposits and withdrawals are not available through the Morpho interface." : "Deposits into this vault are restricted by an active gate and are not available through the Morpho interface.";
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          whiteSpace: "break-spaces"
        };
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      let t5;
      if ($[10] !== t3) {
        t5 = {
          message: t3,
          textStyle: t4
        };
        $[10] = t3;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      t2 = t5;
      break bb0;
    }
    if (error) {
      let t3;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          wordBreak: "break-all"
        };
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      let t4;
      if ($[13] !== error) {
        t4 = {
          message: error,
          textStyle: t3
        };
        $[13] = error;
        $[14] = t4;
      } else {
        t4 = $[14];
      }
      t2 = t4;
      break bb0;
    }
    t2 = undefined;
  }
  return t2;
};
export default useVaultV2TooltipMessage;